import { css } from '@emotion/react'
import { NextPage, NextPageContext } from 'next'
import { mq, colors, font } from 'plume-ui'
import React from 'react'
import { BottomShareMenu } from '@/components/pages/articles/BottomShareMenu'
import { Eyecatch } from '@/components/pages/articles/Eyecatch'
import { FeaturedTOC } from '@/components/pages/articles/FeaturedTOC'
import { GuideForNonArticlePlan } from '@/components/pages/articles/GuideForNonArticlePlan'
import {
  BodyMarkdown,
  IntroMarkdown,
} from '@/components/pages/articles/Markdown'
import { RecommendedArticleList } from '@/components/pages/articles/RecomendedArticleList'
import { RelatedOrganizations } from '@/components/pages/articles/RelatedOrganizations'
import { hasTOC as articleHasTOC, TOC } from '@/components/pages/articles/TOC'
import { TagButton, TagButtonGroup } from '@/components/atoms/TagButton'
import { ArticleHead } from '@/components/common/Head'
import { Template } from '@/components/common/Template'
import { MarketoForm } from '@/components/pages/enterprise/templates/MarketoForm'
import { desktopMinWidth } from '@/components/tokens/mediaquery'
import { useUser } from '../../../contexts/AuthContext'
import {
  contentBodyOf,
  fetchArticle,
  fetchPreviewArticle,
  permittedToRead,
} from '@/lib/article'
import { articlesPath, privacyPolicyPath } from '@/lib/url'
import { Article } from '../../../types'
import Error from '../../_error'
import { isEnterprise } from '@/lib/user'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'
import { A } from '@/components/atoms/A'

type Props = {
  article: Article | undefined
  isPreview: boolean
  slugOrId: string
}

const ArticlePage: NextPage<Props> = (props) => {
  const [article, setArticle] = React.useState<Article | undefined>(
    props.article
  )
  const user = useUser()

  React.useEffect(() => {
    if (props.isPreview) {
      fetchPreviewArticle(props.slugOrId).then((res) => {
        setArticle(res)
      })
    } else {
      setArticle(props.article)
    }
  }, [props.article, props.isPreview, props.slugOrId])

  if (!article) {
    return props.isPreview ? null : <Error statusCode={404} />
  }

  const hasTOC = articleHasTOC(article.body)
  const permitted = permittedToRead(user, article)
  const body = contentBodyOf(article.body, { permitted: permitted })

  return (
    <>
      <Template>
        <ArticleHead article={article}></ArticleHead>
        <div
          css={css`
            ${mq.large} {
              margin-bottom: 64px;
            }
          `}
        >
          <Eyecatch
            content={article}
            previewText={
              props.isPreview
                ? 'この記事はURLを知っている人のみが閲覧できる公開前のプレビューです。URLをSNSなどへシェアしないように注意してください'
                : undefined
            }
            css={css`
              margin-bottom: 32px;
              ${mq.large} {
                margin-bottom: 40px;
              }
            `}
            breadcrumbs={[
              { title: 'ホーム', url: '/' },
              {
                title: 'オリジナル記事',
                url: articlesPath,
              },
              { title: article.title },
            ]}
          />
          <ContentWrapper>
            <IntroMarkdown>{article.introduction}</IntroMarkdown>
          </ContentWrapper>
          {hasTOC && <TOC body={article.body} />}
          <ContentWrapper>
            <BodyMarkdown shrink={!hasTOC}>{body}</BodyMarkdown>
            {isEnterprise(user) ? (
              article.reportFile ? (
                <DownloadAreaWrapper>
                  <div
                    css={css`
                      margin-top: 36px;
                      font-size: 16px;
                      line-height: 1.7;
                      letter-spacing: 0.02em;
                      color: ${colors.text};
                      ${mq.large} {
                        margin-top: 47px;
                      }
                    `}
                  >
                    ［レポートをダウンロード］をクリックすると、レポートをご覧になれます。
                  </div>
                  <div
                    css={css`
                      margin-top: 24px;
                      display: flex;
                      justify-content: center;
                      ${mq.large} {
                        margin-top: 47px;
                      }
                    `}
                  >
                    <a
                      href={article.reportFile.url}
                      css={css`
                        display: block;
                        text-align: center;
                        font-size: 14px;
                        font-weight: ${font.weight.bold};
                        line-height: 1.5;
                        color: ${colors.white00};
                        padding: 13px 70px;
                        background-color: ${colors.black10};
                      `}
                    >
                      <IcomoonIcon
                        name="download"
                        css={css`
                          color: ${colors.cyan70};
                          margin-right: 13px;
                          font-size: 17px;
                          font-weight: ${font.weight.bold};
                        `}
                      ></IcomoonIcon>
                      レポートをダウンロード
                    </a>
                  </div>
                  <div
                    css={css`
                      margin-top: 48px;
                      font-weight: ${font.weight.bold};
                      font-size: 15px;
                      line-height: 1.4;
                      letter-spacing: 0.02em;
                      color: ${colors.textStrong};
                      ${mq.large} {
                        margin-top: 64px;
                        font-size: 16px;
                      }
                    `}
                  >
                    ダウンロードができない場合は？
                  </div>
                  <div
                    css={css`
                      margin-top: 8px;
                      font-size: 14px;
                      line-height: 1.4;
                      letter-spacing: 0.02em;
                      color: ${colors.text};
                    `}
                  >
                    ダウンロードが失敗する場合やご不明な点などございましたら、お手数ですが、
                    <A href="mailto:insidesales@uzabase.com" target="_blank">
                      insidesales@uzabase.com
                    </A>
                    までお問い合わせください。
                  </div>
                </DownloadAreaWrapper>
              ) : null
            ) : article.formCode ? (
              <DownloadAreaWrapper>
                <div
                  css={css`
                    margin-bottom: 24px;
                    margin-top: 36px;
                    font-size: 16px;
                    line-height: 1.7;
                    letter-spacing: 0.02em;
                    ${mq.large} {
                      margin-top: 47px;
                    }
                  `}
                >
                  以下のフォームに必要事項をご入力の上送信いただくと、ダウンロードページが表示されます。
                </div>
                <MarketoForm code={article.formCode} />
                <div
                  css={css`
                    margin-top: 24px;
                    font-size: 13px;
                    line-height: 1.4;
                    color: ${colors.text};
                  `}
                >
                  <p>
                    フォームが表示されない場合は...
                    <br />
                    <A href="mailto:insidesales@uzabase.com">
                      insidesales@uzabase.com
                    </A>
                    までお問い合わせください。
                  </p>
                  <p>
                    弊社では、フォームよりお預かりした個人情報の取扱いについて、次のように管理し保護に努めてまいります。この先続行することで下記に同意したものとします。{' '}
                  </p>
                  <p>
                    【個人情報の収集・利用・提供について】
                    <br />
                    お預かりしました個人情報は、弊社グループにて開催するセミナーイベントのご案内、製品・サービスのご紹介（メールマガジン等）に利用させていただきます。
                  </p>
                  <p>
                    【個人情報保護方針】
                    <br />
                    個人情報取扱い管理の詳細については、以下のリンクをご参照ください。
                    <A href={privacyPolicyPath} target="_blank">
                      {`https://initial.inc${privacyPolicyPath}`}
                    </A>
                  </p>
                </div>
              </DownloadAreaWrapper>
            ) : null}
          </ContentWrapper>
          {permitted ? (
            <ArticleReadTarget article={article}></ArticleReadTarget>
          ) : (
            <ContentWrapper
              id="guide"
              css={css`
                margin-top: -20px;
                padding: 0 20px;
                ${mq.large} {
                  margin-top: 0;
                  padding: 0;
                }
              `}
            >
              <GuideForNonArticlePlan user={user} />
            </ContentWrapper>
          )}
          {!props.isPreview && (
            <div
              css={css`
                margin: 48px 0;
                ${mq.large} {
                  margin: 64px 0;
                }
              `}
            >
              <BottomShareMenu article={article} title={article.title} />
            </div>
          )}
          <ContentWrapper>
            <RelatedOrganizations
              companies={article.companies}
              investors={article.investors}
            />
            <FeaturedTOC article={article} />
          </ContentWrapper>
        </div>
        <RecommendedArticleList articles={article.relatedArticles} />
      </Template>
    </>
  )
}

const ArticleReadTarget: React.FC<{
  article: Article
}> = (props) => {
  const [article, setArticle] = React.useState<Article>(props.article)
  React.useEffect(() => {
    setArticle(props.article)
  }, [props.article])

  return (
    <ContentWrapper id="article-read" key={article.id}>
      {article.newTags && (
        <div
          css={css`
            margin: 14px 24px 30px;
            @media (min-width: ${desktopMinWidth}) {
              margin: 20px 48px 40px;
            }
          `}
        >
          <TagButtonGroup>
            {article.newTags?.map((tag, i) => <TagButton tag={tag} key={i} />)}
          </TagButtonGroup>
        </div>
      )}
    </ContentWrapper>
  )
}

type ContentWrapperProps = {
  id?: string
  children: React.ReactNode
}

const ContentWrapper: React.FC<ContentWrapperProps> = (props) => {
  return (
    <div
      id={props.id}
      css={css`
        background-color: #fff;
        max-width: 736px;
        margin: 0 auto;
      `}
      {...props}
    />
  )
}

type DownloadAreaWrapperProps = {
  children: React.ReactNode
}

const DownloadAreaWrapper: React.FC<DownloadAreaWrapperProps> = (props) => {
  return (
    <div
      css={css`
        margin: 46px 20px 45px;
        padding: 40px 0px 32px;
        position: relative;
        border-top: 1px solid ${colors.border};
        border-bottom: 1px solid ${colors.border};
        ${mq.large} {
          margin: 90px 0 45px;
          padding: 57px 100px 61px;
          box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.12);
          border: none;
        }
      `}
    >
      <div
        css={css`
          font-weight: ${font.weight.bold};
          font-size: 18px;
          line-height: 1.5;
          text-align: center;
          color: ${colors.textStrong};
          ${mq.large} {
            font-size: 21px;
          }
        `}
      >
        レポートはここからダウンロード
      </div>
      <div
        css={css`
          position: relative;
          width: 32px;
          border-top: 1px solid ${colors.textStrong};
          top: 12px;
          margin: 0 auto;
        `}
      />
      {props.children}
    </div>
  )
}

ArticlePage.getInitialProps = async (ctx: NextPageContext) => {
  const { slugOrId, preview } = ctx.query
  const isPreview = preview === '1'
  const article = isPreview ? undefined : await fetchArticle(slugOrId as string)
  if (article) {
    redirectIdToSlug(ctx, article)
  }
  return { article, isPreview, slugOrId: slugOrId as string }
}

const redirectIdToSlug = (ctx: NextPageContext, article: Article) => {
  if (article.slug && ctx.query.slugOrId === article.id && ctx.res) {
    ctx.res.writeHead(301, {
      Location: `/articles/${article.slug}`,
    })
    ctx.res.end()
  }
}

export default ArticlePage
